import { guessUserTimezone } from '@eventbrite/datetime-fns';
import { Icon } from '@eventbrite/eds-icon';
import { ChevronDownChunky } from '@eventbrite/eds-iconography';
import { Layout } from '@eventbrite/eds-layout';
import { gettext } from '@eventbrite/i18n';
import { requestIdleCallbackWithPolyfill } from '@eventbrite/request-idle-callback';
import React from 'react';
import { connect } from 'react-redux';
import { IBrowseFilters } from '../../../../constants/filters';
import { ALL_TAB, FEED_TABS, FOR_YOU } from '../../../../constants/tabConfig';
import {
    GetEnrollmentStatus,
    useGetStatsigEnrollment,
} from '../../../../hooks';
import {
    ConnectedProps,
    DiscoverState,
    SeasonalContents,
} from '../../../../types/index';
import { StickyTabs } from '../../components/Tabs';
import { HomeEngagementExperimentTrackingLocations } from '../../constants/analytics';
import { useBrowseFilters } from '../../hooks/BrowseFilters';
import { fetchEventsForTab, fetchUserSavedEvents } from '../../redux/events/';
import { injectSeasonalTab, scrollIt } from '../../utils';
import { logToStatsigHomeEngagement } from '../../utils/analytics';
import { FeedBuilderContainer } from '../FeedBuilderContainer';
import { HomeLocationAutocompleteContainer } from '../HomeLocationAutocompleteContainer';
import { ImageCategoryBrowse } from '../ImageCategoryBrowse';
import './BrowseContainer.scss';
import { ForYouTabKey } from './ForYouTabKey';
const Chevron = (
    <Icon type={<ChevronDownChunky />} color="ui-blue--hover" size="medium" />
);

export const ONLINE_EVENTS_INPUT_COPY = gettext('Popular ');

const LOCATION_AUTOCOMPLETE_PROPS = {
    prefix: Chevron,
    borderType: 'content',
    id: 'locationPicker',
    name: 'locationPicker',
    hideOutline: true,
    isLimitedSearch: true,
    preventInputValuePrefill: true,
    shouldRemoveConflictiveAria: true,
};

const shouldIncludeCurrentTab = ({
    tabKey,
    tabsConfig,
}: {
    tabKey?: string;
    tabsConfig: { [key: string]: any };
}) => {
    if (!tabKey || !tabsConfig[tabKey]) {
        return false;
    }

    return true;
};

const getBrowseTabs = (
    seasonalContent?: SeasonalContents,
    weekendGuideExperimentVariant?: boolean | string,
) => {
    const tabsConfig = injectSeasonalTab(seasonalContent);
    const tabs: {
        tabKey: string;
        value: number;
        displayName: string;
        content: JSX.Element;
    }[] = [];

    FEED_TABS.forEach((tabKey, index) => {
        const shouldAddTab = shouldIncludeCurrentTab({
            tabKey,
            tabsConfig,
        });

        if (shouldAddTab) {
            const { name, eventSearch, follow, feedConfig } =
                tabsConfig[tabKey];
            let displayName = name;

            if (tabKey === FOR_YOU) {
                displayName = <ForYouTabKey />;
            }

            tabs.push({
                tabKey,
                value: index,
                displayName: displayName,
                content: (
                    <FeedBuilderContainer
                        feedConfig={feedConfig}
                        eventSearch={eventSearch}
                        follow={follow}
                        tabKey={tabKey}
                        weekendGuideExperimentVariant={
                            weekendGuideExperimentVariant
                        }
                    />
                ),
            });
        }
    });

    return tabs;
};

const LocationComponent = ({ location }: { location?: object }) => {
    const copy = gettext('Popular in ');

    // Homepage location pill experiment clean up. EB-229970

    return (
        <div className="browse__location eds-l-pad-top-4">
            <div className="browse__location-text">{copy}</div>
            <HomeLocationAutocompleteContainer
                autocompleteProps={LOCATION_AUTOCOMPLETE_PROPS}
                shouldDispatchSearchActions={true}
                location={location}
            />
        </div>
    );
};

const handleTabLoad = (
    tabKey: string,
    {
        fetchEventsForTab,
        isFeLoading,
        filters,
    }: {
        fetchEventsForTab: Function;
        isFeLoading: boolean;
        filters?: IBrowseFilters[];
    },
) => {
    /**
     * On first load we do not need to fetch new events ONLY for the all_tab.
     * The tab is loaded before the `fe` has finished fetching so we use that
     * variable to know if we are in the all tab to prevent fetching.
     */
    if ((!isFeLoading && tabKey === ALL_TAB) || tabKey !== ALL_TAB) {
        fetchEventsForTab({ tabKey, shouldUpdateContent: true, filters });
    }
};

const handleTabChange = (
    tabKey: string,
    {
        fetchEventsForTab,
        filters,
    }: {
        fetchEventsForTab: Function;
        filters?: IBrowseFilters[];
    },
) => {
    scrollIt(
        ((document.querySelector('.browse-section') as HTMLElement)
            ?.offsetTop || 0) + 70,
    );

    fetchEventsForTab({ tabKey, shouldUpdateContent: true, filters });
};

const getUserTimezoneFilter = (
    weekendGuideExperimentVariant: boolean | string,
) => {
    if (weekendGuideExperimentVariant) {
        return [
            {
                name: 'timezone',
                value: guessUserTimezone(),
                payload: 'data',
            },
        ];
    }

    return undefined;
};

const Browse = (props: BrowseProps) => {
    const {
        tabKey,
        location,
        seasonalContent,
        isFeLoading,
        isNewHeader,
        fetchEventsForTab,
        fetchUserSavedEvents,
        isAuthenticated,
    } = props;

    //TODO: Pass proper enum in for expected variant values
    const weekendGuideExperiment = useGetStatsigEnrollment<string | boolean>({
        name: 'weekend_guide_bucket',
        paramName: 'show_weekend_guide',
        defaultValue: false,
    });

    const weekendGuideExperimentVariant =
        weekendGuideExperiment.status === GetEnrollmentStatus.SUCCESS
            ? weekendGuideExperiment.variant
            : GetEnrollmentStatus.LOADING;

    const userTimezoneFilter = getUserTimezoneFilter(
        weekendGuideExperimentVariant,
    );

    const dateFilter = useBrowseFilters(fetchEventsForTab);
    const items = getBrowseTabs(seasonalContent, weekendGuideExperimentVariant);
    const hasFetchedEventsRef = React.useRef(false);

    React.useEffect(() => {
        if (!hasFetchedEventsRef.current && tabKey === FOR_YOU) {
            hasFetchedEventsRef.current = true;
            requestIdleCallbackWithPolyfill(() => fetchUserSavedEvents());
        }
    }, [fetchUserSavedEvents, tabKey]);

    return (
        <div className="browse-section" id="browse-section">
            <ImageCategoryBrowse />
            <Layout hasHorizontalGutters={true} maxWidth="large">
                <LocationComponent location={location} />
            </Layout>

            <StickyTabs
                items={items}
                isNewHeader={isNewHeader}
                onTabChange={(tabKey: string) => {
                    const trackingLocation =
                        tabKey === FOR_YOU
                            ? HomeEngagementExperimentTrackingLocations.FOR_YOU
                            : HomeEngagementExperimentTrackingLocations.TAB;

                    logToStatsigHomeEngagement(
                        trackingLocation,
                        isAuthenticated,
                    );

                    handleTabChange(tabKey, {
                        fetchEventsForTab,
                        filters: userTimezoneFilter,
                    });
                }}
                onTabLoad={(tabKey: string) =>
                    handleTabLoad(tabKey, {
                        fetchEventsForTab,
                        isFeLoading,
                        filters: userTimezoneFilter,
                    })
                }
                initialSelectedTab={tabKey}
                location={location}
                dateFilter={dateFilter}
            />
        </div>
    );
};

const _mapStateToProps = (state: DiscoverState) => ({
    isAuthenticated: Boolean(state.user.isAuthenticated),
    tabKey: state.content.browseState?.tabKey,
    isFeLoading: Boolean(state.app.loadingState?.isFeLoading),
    location: state.location,
    seasonalContent: state.content.seasonal,
    isNewHeader: state.app.featureFlags?.launchHeader2021,
});

const _mapDispatchToProps = {
    fetchEventsForTab,
    fetchUserSavedEvents,
};

type BrowseProps = ConnectedProps<
    typeof _mapStateToProps,
    typeof _mapDispatchToProps
>;

export default connect(_mapStateToProps, _mapDispatchToProps)(Browse);
